import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getReactNativePersistence } from "firebase/auth/react-native";
import {
 getDatabase,
 ref as databaseRef,
 onValue,
 set,
 remove,
 update as firebaseUpdate,
 get,
 goOffline,
 goOnline,
 onDisconnect,
} from "firebase/database";
import {
 getDownloadURL,
 getStorage,
 ref as storageRef,
 uploadBytes,
 deleteObject,
} from "firebase/storage";
import {
 signInWithEmailAndPassword,
 signOut,
 sendPasswordResetEmail,
 initializeAuth,
} from "firebase/auth";
import AsyncStorage from "@react-native-async-storage/async-storage";
import "react-native-get-random-values";
import { nanoid } from "nanoid";
import { setupValidator } from "../services/validator";
import { useApp } from "../App";
import baseTheme from "../services/baseTheme";
import { cloneDeep } from "lodash";

const DatabaseContext = createContext({});

// salva algo no local storage
async function saveLocalStorage(key, value) {
 try {
  await AsyncStorage.setItem(key, value);
  return true;
 } catch (e) {
  console.log(e);
  return false;
 }
}

// recupera algo do local storage
async function getLocalStorage(key) {
 let result = null;
 await AsyncStorage.getItem(key, (e, res) => {
  if (e) {
   console.log(e);
   return;
  }
  result = res;
 });
 return result;
}

export function FirebaseDatabase({ children }) {
 const { updateTheme } = useApp();

 // usado para saber se devemos mostrar a splash screen ou nao
 // usamos na abertura do app para mostrar a splash enquanto carregamos dados
 // da storage (key do cliente), e fazemos o login pelo firebase
 const [showSplashScreen, setShowSplashScreen] = useState(true);
 const [isClientDisabled, setIsClientDisabled] = useState(false);

 const [isConnected, setIsConnected] = useState(false); // está conectado ao firebase do cliente
 const [isTrial, setIsTrial] = useState(""); // se o cliente é uma versao trial ou paga
 const [systemConfig, setSystemConfig] = useState(null); // configuração do sistema do cliente
 const [logoUrl, setLogoUrl] = useState(null); // logo do cliente

 const [user, setUser] = useState(null); // usuario conectado
 const [isAdmin, setIsAdmin] = useState(false); // usuario conectado é admin?

 const [data, setData] = useState({}); // dados da lista
 const [info, setInfo] = useState(""); // dados da pagina de informações
 const [users, setUsers] = useState(null); // usuarios do sistema (adicionados no firebase)
 const [pushTokens, setPushTokens] = useState({}); // push tokens dos usuarios
 const [customLists, setCustomLists] = useState({}); // listas customizadas

 // variaveis do firebase
 const [firebaseApp, setFirebaseApp] = useState(null);
 const [firebaseDb, setFirebaseDb] = useState(null);
 const [firebaseStorage, setFirebaseStorage] = useState(null);
 const [firebaseAuth, setFirebaseAuth] = useState(null);

 const mappedCustomLists = useMemo(() => {
  const mapped = {};
  if (systemConfig?.customLists && customLists) {
   Object.keys(customLists).forEach((listAlias) => {
    const listConfig = systemConfig.customLists.find(({ alias }) => alias === listAlias);
    if (!listConfig) return;
    mapped[listAlias] = {
     ...listConfig,
     options: customLists[listAlias],
    };
   });
  }
  return mapped;
 }, [customLists, systemConfig]);

 const logout = useCallback(() => {
  signOut(firebaseAuth);
 }, [firebaseAuth]);

 // se tivermos acesso ao banco, iremos criar os listeners dos dados
 useEffect(() => {
  let mounted = true;
  if (!user) return;

  const listeners = [];
  console.log("Iniciando Escuta");

  const db = getDatabase(firebaseApp);

  // tenta manter a conexão com o banco quando a mesma se desconecta.
  // Isso tenta resolver um bug onde o banco se desconecta e não volta mais
  // em uma certa rede wi-fi (https://github.com/firebase/firebase-android-sdk/issues/1258)
  const connectedRef = databaseRef(db, ".info/connected");
  onValue(connectedRef, (snapshot) => {
   if (snapshot.val() !== true) {
    if (!mounted) return;
    console.log("Cliente desconectado... Tentando reconectar...");
    goOffline(db);
    goOnline(db);
    // setTimeout(() => {
    //  console.log("Conectando Novamente");
    //  goOnline(db);
    // }, 1000);
   } else console.log("Cliente conectado com sucesso!");
  });

  // console.log("Ficando Offline");
  // goOffline(db);

  // console.log("Ficando Online");
  // goOnline(db);

  const dataRef = databaseRef(db, `${isTrial}/data`);

  listeners.push(
   onValue(dataRef, (snapshot) => {
    const newData = snapshot.val();
    console.log("Novos Dados");
    if (newData) setData(newData);
    else setData({});
   })
  );

  const infoRef = databaseRef(db, `${isTrial}/info`);
  listeners.push(
   onValue(infoRef, (snapshot) => {
    const newData = snapshot.val();
    if (newData) setInfo(newData);
    else setInfo("");
   })
  );

  const usersRef = databaseRef(db, `${isTrial}/users`);
  listeners.push(
   onValue(usersRef, (snapshot) => {
    const newData = snapshot.val();
    if (newData) setUsers(newData);
    else setUsers({});
   })
  );

  const tokensRef = databaseRef(db, `${isTrial}/pushToken`);
  listeners.push(
   onValue(tokensRef, (snapshot) => {
    const newData = snapshot.val();
    if (newData) setPushTokens(newData);
    else setPushTokens({});
   })
  );

  const listsRef = databaseRef(db, `${isTrial}/customLists`);
  listeners.push(
   onValue(listsRef, (snapshot) => {
    const newData = snapshot.val();
    if (newData) setCustomLists(newData);
    else setCustomLists([]);
   })
  );

  setFirebaseDb(db);

  return () => {
   mounted = false;
   listeners.forEach((listener) => {
    listener();
   });
  };
 }, [firebaseApp, user, isTrial]);

 // Apos conseguirmos os listeners
 // iremos verificar se o usuario atual é um admin e se ele ja esta no banco de usuarios
 // do firebase. Se nao estiver, iremos adiciona-lo como usuario comum
 useEffect(() => {
  if (!firebaseDb || !users) return;
  if (!user) setIsAdmin(false);
  else if (!users[user.uid]) {
   if (isTrial) {
    if (Object.keys(users).length > 0) {
     logout();
     return;
    }
   }

   setIsAdmin(false);
   const insertRef = databaseRef(firebaseDb, `${isTrial}/users/${user.uid}`);
   set(insertRef, {
    email: user.email,
    admin: false,
   });
  } else if (!users[user.uid].admin) setIsAdmin(false);
  else setIsAdmin(true);
 }, [user, users, firebaseDb, isTrial, logout]);

 // Iremos tentar nos conectar ao firebase do cliente,
 // e se tivermos sucesso, iremos iniciar o setup para
 // adaptar o app
 const findClientDatabase = useCallback(
  async (key) => {
   key = key.toLowerCase();

   const data = await fetch(`https://managfy.netlify.app/.netlify/functions/client?name=${key}`)
    .catch((e) => {
     console.log(e);
    })
    .then(async (res) => {
     if (res.status !== 200) {
      const error = await res.text();
      if (error === "O cliente não existe")
       throw new Error(
        "O cliente não foi encontrado, verifique a chave digitada e tente novamente"
       );
      else throw new Error(error);
     }
     return res.json();
    });

   if (!data) {
    setShowSplashScreen(false);
    return false;
   }

   if (data.trial) {
    setIsTrial(`/${key}`);
    data.firebaseConfig = trialFirebaseConfig;
   } else setIsTrial("");

   if (data.disabled) {
    setIsClientDisabled(true);
    return false;
   }

   // salvar configs
   setSystemConfig(data.appConfig);
   //  setSystemConfig(testConfig);

   setLogoUrl(data.logo);
   saveLocalStorage("clientLogo", data.logo);

   if (data.appConfig.lightTheme) updateTheme(data.appConfig.lightTheme, "light");
   else updateTheme(baseTheme.lightTheme, "light");
   if (data.appConfig.darkTheme) updateTheme(data.appConfig.darkTheme, "dark");
   else updateTheme(baseTheme.darkTheme, "dark");

   // cria o validator do ajv com base no schema da configuração
   setupValidator(data.appConfig);
   //  setupValidator(testConfig);

   const app = initializeApp(data.firebaseConfig, "client");
   const auth = initializeAuth(app, {
    persistence: getReactNativePersistence(AsyncStorage),
   });

   console.log("Atualizando App");
   setFirebaseApp(app);

   const storage = getStorage(app);
   setFirebaseStorage(storage);

   setFirebaseAuth(auth);

   // criar listener de login do usuario
   // quando alguem logar, iremos setar o user
   auth.onAuthStateChanged((user) => {
    if (user) setUser(user);
    else setUser(null);
    setShowSplashScreen(false);
   });

   // salvaremos a chave do cliente para não precisar digitar de novo
   // na proxima abertura do app
   saveLocalStorage("clientKey", key);

   setIsConnected(true);
  },
  [updateTheme]
 );

 const signIn = useCallback(
  /**
   * @param {string} email
   * @param {string} password
   */
  async (email, password) => {
   const data = {
    success: false,
    message: "Houve um problema inesperado!",
   };

   try {
    await signInWithEmailAndPassword(firebaseAuth, email.trim(), password.trim());
    data.success = true;
   } catch (error) {
    if (error.code === "auth/missing-email" || error.code === "auth/invalid-email")
     data.message = "Email informado incorretamente";
    else if (error.code === "auth/user-not-found" || error.code === "auth/wrong-password")
     data.message = "Email e/ou senha incorretos";
    else data.message = `${error.message} - ${error.code}`;
   }

   return data;
  },
  [firebaseAuth]
 );

 const forgotPassword = useCallback(
  async (email) => {
   return sendPasswordResetEmail(firebaseAuth, email);
  },
  [firebaseAuth]
 );

 const getArchived = useCallback(async () => {
  const archived = await get(databaseRef(firebaseDb, `${isTrial}/archived`)).then((snapshot) => {
   return snapshot.val();
  });

  if (archived) return archived;
  else return {};
 }, [firebaseDb, isTrial]);

 const canInsert = useCallback(async () => {
  if (isTrial) {
   if (Object.keys(data).length >= 50) {
    window.toast("Você não pode inserir mais de 50 itens na versão gratuita.");
    return false;
   }

   const archived = await getArchived();
   if (Object.keys(archived).length + Object.keys(data).length >= 50) {
    window.toast("Você não pode inserir mais de 50 itens na versão gratuita.");
    return false;
   }
  }

  return true;
 }, [isTrial, getArchived, data]);

 // insere um dado em data
 const insert = useCallback(
  async (data) => {
   const can = await canInsert();
   if (!can) return false;

   const id = nanoid();
   const insertRef = databaseRef(firebaseDb, `${isTrial}/data/${id}`);
   await set(insertRef, { ...data, createDate: new Date().toISOString() });
   return id;
  },
  [firebaseDb, isTrial, canInsert]
 );

 // atualizaum dado em data
 const update = useCallback(
  async (id, data) => {
   const insertRef = databaseRef(firebaseDb, `${isTrial}/data/${id}`);
   await set(insertRef, data);
   return id;
  },
  [firebaseDb, isTrial]
 );

 const removeItem = useCallback(
  async (id) => {
   const updateRef = databaseRef(firebaseDb, `${isTrial}/data/${id}`);
   await remove(updateRef);
  },
  [firebaseDb, isTrial]
 );

 const unarchiveItems = useCallback(
  async (list) => {
   const copy = cloneDeep(list);

   const updatesTranfer = {};
   const updatesDelete = {};

   for (let i = 0; i < copy.length; i++) {
    const element = copy[i];
    const id = element.id;
    delete element.id;

    updatesDelete[id] = null;
    updatesTranfer[id] = element;
   }

   window.toast(`Desarquivando ${Object.keys(updatesTranfer).length} itens, aguarde...`);

   const dataRef = databaseRef(firebaseDb, `${isTrial}/data`);
   await firebaseUpdate(dataRef, updatesTranfer);

   const archivedRef = databaseRef(firebaseDb, `${isTrial}/archived`);
   await firebaseUpdate(archivedRef, updatesDelete);

   window.toast(`Itens desarquivados com sucesso!`);
  },
  [firebaseDb, isTrial]
 );

 const archiveItems = useCallback(
  async (list) => {
   const copy = cloneDeep(list);

   const updatesTranfer = {};
   const updatesDelete = {};

   for (let i = 0; i < copy.length; i++) {
    const element = copy[i];
    const id = element.id;
    delete element.id;

    updatesDelete[id] = null;
    updatesTranfer[id] = element;
   }

   window.toast(`Arquivando ${Object.keys(updatesTranfer).length} itens, aguarde...`);

   const archivedRef = databaseRef(firebaseDb, `${isTrial}/archived`);
   await firebaseUpdate(archivedRef, updatesTranfer);

   const dataRef = databaseRef(firebaseDb, `${isTrial}/data`);
   await firebaseUpdate(dataRef, updatesDelete);

   window.toast(`Itens arquivados com sucesso!`);
  },
  [firebaseDb, isTrial]
 );

 // atualizaum dado em data
 const updateCustomList = useCallback(
  async (alias, data) => {
   const insertRef = databaseRef(firebaseDb, `${isTrial}/customLists/${alias}`);
   await set(insertRef, data);
   return alias;
  },
  [firebaseDb, isTrial]
 );

 // atualiza a página de informações
 const updateInfo = useCallback(
  async (data) => {
   const insertRef = databaseRef(firebaseDb, `${isTrial}/info`);
   await set(insertRef, data);
  },
  [firebaseDb, isTrial]
 );

 // atualiza o push token de notificações de um usuario
 const updatePushToken = useCallback(
  async (data) => {
   const insertRef = databaseRef(firebaseDb, `${isTrial}/pushToken/${user.uid}`);
   await set(insertRef, data);
  },
  [firebaseDb, user, isTrial]
 );

 // faz o upload de um arquivo
 const upload = useCallback(
  async (uri) => {
   const id = nanoid(); // geramos o id dele

   // transformar em blob e fazer o upload
   const res = await fetch(uri);
   const blob = await res.blob();
   const uploadRef = storageRef(firebaseStorage, `${isTrial ? `${isTrial}/` : ""}${id}`);
   await uploadBytes(uploadRef, blob);
   const newUri = await getDownloadURL(uploadRef);
   return {
    id,
    uri: newUri,
   };
  },
  [firebaseStorage, isTrial]
 );

 // exclui um upload do storage
 const deleteUpload = useCallback(
  async (id) => {
   const uploadRef = storageRef(firebaseStorage, `${isTrial ? `${isTrial}/` : ""}${id}`);
   await deleteObject(uploadRef);
  },
  [firebaseStorage, isTrial]
 );

 // muda o status de administrador de um usuário
 const toggleAdmin = useCallback(
  async (uid, giveAdmin = false) => {
   const updateRef = databaseRef(firebaseDb, `${isTrial}/users/${uid}/admin`);
   await set(updateRef, giveAdmin);
  },
  [firebaseDb, isTrial]
 );

 // remove um usuario (isso não remove o usuario de fazer login, somente do banco)
 const removeUser = useCallback(
  async (uid) => {
   const client = await getLocalStorage("clientKey");
   const token = await user.getIdToken();

   await fetch(
    `https://managfy.netlify.app/.netlify/functions/delete-user?name=${client}&token=${token}&userId=${uid}`,
    {
     method: "DELETE",
    }
   )
    .then(async (res) => {
     if (res.status !== 204) {
      window.toast("Erro Inesperado! Entre em contato com o desenvolvedor.");
      return;
     }

     const updateRef = databaseRef(firebaseDb, `${isTrial}/users/${uid}`);
     await remove(updateRef);
    })
    .catch((e) => {
     window.toast("Erro: " + e.message);
    });
  },
  [firebaseDb, user, isTrial]
 );

 // tenta recuperar a chave de cliente do localstorage
 useEffect(() => {
  if (!isConnected) {
   getLocalStorage("clientLogo").then((value) => {
    if (value) setLogoUrl(value);
   });

   getLocalStorage("clientKey").then((value) => {
    if (value) findClientDatabase(value);
    else setShowSplashScreen(false);
   });
  }
 }, [findClientDatabase, isConnected]);

 return (
  <DatabaseContext.Provider
   value={{
    systemConfig,
    findClientDatabase,
    saveLocalStorage,
    getLocalStorage,
    isConnected,
    logoUrl,
    signIn,
    signOut: logout,
    insert,
    canInsert,
    update,
    upload,
    deleteUpload,
    user,
    data,
    info,
    updateInfo,
    isAdmin,
    users,
    toggleAdmin,
    removeUser,
    showSplashScreen,
    updatePushToken,
    pushTokens,
    customLists,
    mappedCustomLists,
    updateCustomList,
    removeItem,
    forgotPassword,
    archiveItems,
    getArchived,
    unarchiveItems,
    isTrial: !!isTrial,
    isClientDisabled,
   }}>
   {children}
  </DatabaseContext.Provider>
 );
}

/**
 * @returns {{
 * findClientDatabase: Function()Promise<object>,
 * saveLocalStorage: Function,
 * getLocalStorage: Function,
 * systemConfig: object
 * isConnected: boolean,
 * logoUrl: string,
 * signIn: signIn,
 * signOut: Promise<object>,
 * insert: insert,
 * canInsert: canInsert,
 * update: update,
 * upload: upload,
 * user: object,
 * data: object,
 * info: string,
 * isAdmin: boolean,
 * users: object,
 * updateInfo: updateInfo,
 * toggleAdmin: toggleAdmin,
 * removeUser: removeUser,
 * removeItem: removeItem,
 * showSplashScreen: boolean,
 * pushTokens: object,
 * updatePushToken: updatePushToken,
 * mappedCustomLists: object
 * forgotPassword: Function,
 * isClientDisabled: boolean
 * }}
 */
export default function useDatabase() {
 const context = useContext(DatabaseContext);
 return context;
}

/**
 * @callback insert
 * @param {object} data
 * @returns {Promise<string>}
 */

/**
 * @callback updatePushToken
 * @param {string} token
 * @returns {Promise<string>}
 */

/**
 * @callback update
 * @param {string} id
 * @param {object} data
 * @returns {Promise<string>}
 */

/**
 * @callback updateInfo
 * @param {string} data
 * @returns {Promise<void>}
 */

/**
 * @callback signIn
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{success: boolean, message: string}>}
 */

/**
 * @callback upload
 * @param {string} uri
 * @param {string} extension
 * @returns {Promise<{id: string, uri: string}>}
 */

/**
 * @callback toggleAdmin
 * @param {string} uid
 * @param {boolean} giveAdmin
 * @returns {Promise<void>}
 */

/**
 * @callback removeUser
 * @param {string} uid
 * @returns {Promise<void>}
 */

/**
 * @callback removeItem
 * @param {string} id
 * @returns {Promise<void>}
 */

const trialFirebaseConfig = {
 apiKey: "AIzaSyDg8PkvXRfa3f2gLJ35kuiduhNkGhu4ntE",
 authDomain: "managfy-free-tier.firebaseapp.com",
 databaseURL: "https://managfy-free-tier-default-rtdb.firebaseio.com",
 projectId: "managfy-free-tier",
 storageBucket: "managfy-free-tier.appspot.com",
 messagingSenderId: "1083602680034",
 appId: "1:1083602680034:web:ee61c751519fa2914a46bb",
};

const testConfig = {
 schema: {
  type: "object",
  properties: {
   status: { enum: ["aguardando", "fechado", "fechadop", "enviado", "negado", "semretorno"] },
   data: { type: "string", nullable: true },
   cpf: { type: "string", nullable: true },
   cliente: { type: "string", isNotEmpty: true },
   responsavel: { type: "string", isNotEmpty: true },
   cidade: { type: "string", isNotEmpty: true },
   endereco: { type: "string", isNotEmpty: true },
   complemento: { type: "string", nullable: true },
   telefone: { type: "string", isNotEmpty: true },
   medidas: {
    type: "array",
    items: {
     type: "object",
     properties: {
      tipo: { type: "string", nullable: true },
      nome: { type: "string", isNotEmpty: true },
      medidas: { type: "string", isNotEmpty: true },
      sugestao: { type: "string", nullable: true },
      observacoes: { type: "string", nullable: true },
      valor: { type: "string", nullable: true },
      status: { enum: ["aguardando", "fechado", "enviado", "negado"] },
      fotos: { type: "array" },
     },
     required: ["tipo", "valor", "nome", "medidas", "sugestao", "observacoes", "status", "fotos"],
    },
   },
  },
  required: [
   "status",
   "data",
   "responsavel",
   "cliente",
   "cidade",
   "endereco",
   "complemento",
   "telefone",
   "medidas",
   "cpf",
  ],
 },
 fields: {
  status: {
   category: "Dados Iniciais",
   label: "Status",
   type: "list",
   icon: "circle-slice-3",
   defaultValue: "aguardando",
   options: [
    { label: "Aguardando", value: "aguardando" },
    { label: "Fechado", value: "fechado" },
    { label: "Fechado Parcialmente", value: "fechadop" },
    { label: "Enviado", value: "enviado" },
    { label: "Negado", value: "negado" },
    { label: "Sem Retorno", value: "semretorno" },
   ],
   filterable: true,
  },
  data: {
   category: "Dados Iniciais",
   label: "Data",
   defaultValue: "today",
   type: "date",
   icon: "calendar",
   isDateFilter: true,
  },
  arquivo: {
   category: "Dados Iniciais",
   label: "Arquivo",
   type: "file",
   icon: "account",
  },
  responsavel: {
   category: "Dados Iniciais",
   label: "Funcionário Responsável",
   type: "owner",
   icon: "account",
   isRequired: true,
  },
  pagamento: {
   category: "Dados Iniciais",
   label: "Forma de Pagamento",
   type: "formaPagamento",
   icon: "cash",
  },
  cliente: {
   category: "Cliente",
   label: "Nome Completo",
   type: "string",
   icon: "account",
   isRequired: true,
   searchable: true,
  },
  cpf: {
   category: "Cliente",
   label: "CPF",
   type: "string",
   icon: "card-account-details-outline",
   searchable: true,
  },
  cidade: {
   category: "Cliente",
   label: "Cidade",
   type: "string",
   icon: "city",
   isRequired: true,
  },
  endereco: {
   category: "Cliente",
   label: "Endereço",
   type: "address",
   icon: "map-marker",
   searchable: true,
   isRequired: true,
  },
  complemento: {
   category: "Cliente",
   label: "Complemento",
   type: "string",
   icon: "home-city",
  },
  telefone: {
   category: "Cliente",
   label: "Telefone",
   type: "phone",
   icon: "phone",
   isRequired: true,
   searchable: true,
  },
  medidas: {
   category: "Medidas",
   label: "Cômodo",
   type: "dataList",
   fields: {
    nome: { label: "Nome", type: "string", isRequired: true },
    medidas: { label: "Medidas", type: "string", lines: 3, isRequired: true },
    tipo: { label: "Tipo", type: "tipoProduto", isRequired: true },
    sugestao: { label: "Sugestão de Produto", type: "string" },
    valor: { label: "Valor", type: "currency" },
    observacoes: { label: "Observações", type: "string" },
    status: {
     label: "Status",
     type: "list",
     defaultValue: "aguardando",
     options: [
      { label: "Aguardando", value: "aguardando" },
      { label: "Fechado", value: "fechado" },
      { label: "Enviado", value: "enviado" },
      { label: "Negado", value: "negado" },
     ],
    },
    fotos: { label: "Fotos", type: "imageList" },
   },
  },
 },
 list: {
  listTitle: "Orçamentos",
  header: "cliente",
  avatar: null,
  subHeader: "telefone",
  body: "endereco",
  baseFilter: { key: "status", value: "aguardando" },
  status: {
   key: "status",
   defaultColor: "#9fa19f",
   colors: [
    { color: "#ff0044", operator: "=", value: "negado" },
    { color: "#13c0d4", operator: "=", value: "fechado" },
    { color: "#1862c9", operator: "=", value: "fechadop" },
    { color: "#ffd500", operator: "=", value: "aguardando" },
    { color: "#18c927", operator: "=", value: "enviado" },
    { color: "#707070", operator: "=", value: "semretorno" },
   ],
  },
 },
 calendarIntegration: {
  type: "google",
  icon: "google-plus",
  event: "Visita técnica com {cliente}",
  location: "{endereco}",
  duration: 60,
  dateField: "data",
 },
 saveFilterLocally: true,
 pdfTemplate:
  '<html lang="pt"> <head> <meta charset="utf-8"/> <meta name="viewport" content="width=device-width, initial-scale=1.0"/> <style type="text/css"> *{margin: 0; padding: 0; text-indent: 0;}.s1{color: black; font-family: Calibri, sans-serif; font-style: normal; font-weight: normal; text-decoration: none; font-size: 11pt; text-align: center;}.s2{color: black; font-family: Calibri, sans-serif; font-style: normal; font-weight: bold; text-decoration: none; font-size: 18pt; text-align: center;}.s3{color: black; font-family: Calibri, sans-serif; font-style: normal; font-weight: bold; text-decoration: none; font-size: 11pt; text-align: center;}.s4{color: black; font-family: Calibri, sans-serif; font-style: normal; font-weight: bold; font-size: 11pt; text-align: center;}.td0{border-top-style: solid; border-top-width: 1pt; border-left-style: solid; border-left-width: 1pt; border-bottom-style: solid; border-bottom-width: 1pt; border-right-style: solid; border-right-width: 1pt; vertical-align: middle;}table, tbody{vertical-align: top; overflow: visible;}tr:nth-child(15n){page-break-after: always;}</style> </head> <body> <div style=" width: 700px; margin-left: auto; margin-right: auto; display: flex; flex-direction: column; align-items: center; justify-content: center; " ><table style="width: 700px; border-collapse: collapse" cellspacing="0"><tr style="height: 14pt"> <td class="td0"><p class="s1">DATA:</p></td><td class="td0" colspan="3"><p class="s1">{data}</p></td></tr><tr style="height: 14pt"> <td class="td0"><p class="s1">FORMA DE PAGAMENTO:</p></td><td class="td0" colspan="3"><p class="s1">{pagamento}</p></td></tr><tr style="height: 14pt"> <td class="td0"><p class="s1">CLIENTE:</p></td><td class="td0" colspan="3"><p class="s1">{cliente}</p></td></tr><tr style="height: 14pt"> <td class="td0"><p class="s1">ENDEREÇO:</p></td><td class="td0" colspan="3"><p class="s1">{endereco}</p></td></tr><tr style="height: 14pt"> <td class="td0"><p class="s1">MUNICÍPIO:</p></td><td class="td0" colspan="3"><p class="s1">{cidade}</p></td></tr><tr style="height: 14pt"> <td class="td0"><p class="s1">TELEFONE:</p></td><td class="td0" colspan="3"><p class="s1">{telefone}</p></td></tr><tr style="height: 14pt"> <td class="td0"><p class="s1">PROF. RESP.:</p></td><td class="td0" colspan="3"><p class="s1">{responsavel}</p></td></tr><tr style="height: 28pt"> <td class="td0" colspan="4"><p class="s2">ORÇAMENTO</p></td></tr><tr style="height: 43pt"> <td class="td0" style="background-color: #bebebe"><p class="s1">AMBIENTE</p></td><td class="td0" style="background-color: #bebebe"> <p class="s1" style="padding-left: 130pt; padding-right: 130pt">ITEM</p></td><td class="td0" style="background-color: #bebebe"> <p class="s1" style="padding-left: 5pt; padding-right: 5pt">VALOR</p></td></tr>[medidas__ <tr style="height: 36pt"> <td class="td0"><p class="s1">{item.nome}</p></td><td class="td0"><p class="s1">{item.sugestao}</p></td><td class="td0"><p class="s1">R${item.valor}</p></td></tr>]</table> </div></body></html>',
 customLists: [
  { alias: "owner", label: "Responsáveis", type: "single" },
  { alias: "tipoProduto", label: "Tipo", type: "single" },
  { alias: "formaPagamento", label: "Formas de Pagamento", type: "single" },
 ],
 lightTheme: {
  colors: {
   primary: "rgb(118, 91, 0)",
   onPrimary: "rgb(255, 255, 255)",
   primaryContainer: "rgb(255, 223, 147)",
   onPrimaryContainer: "rgb(36, 26, 0)",
   secondary: "rgb(119, 90, 0)",
   onSecondary: "rgb(255, 255, 255)",
   secondaryContainer: "rgb(255, 223, 151)",
   onSecondaryContainer: "rgb(37, 26, 0)",
   tertiary: "rgb(0, 100, 148)",
   onTertiary: "rgb(255, 255, 255)",
   tertiaryContainer: "rgb(202, 230, 255)",
   onTertiaryContainer: "rgb(0, 30, 48)",
   error: "rgb(186, 26, 26)",
   onError: "rgb(255, 255, 255)",
   errorContainer: "rgb(255, 218, 214)",
   onErrorContainer: "rgb(65, 0, 2)",
   background: "rgb(255, 251, 255)",
   onBackground: "rgb(30, 27, 22)",
   surface: "rgb(255, 251, 255)",
   onSurface: "rgb(30, 27, 22)",
   surfaceVariant: "rgb(236, 225, 207)",
   onSurfaceVariant: "rgb(76, 70, 57)",
   outline: "rgb(126, 118, 103)",
   outlineVariant: "rgb(207, 197, 180)",
   shadow: "rgb(0, 0, 0)",
   scrim: "rgb(0, 0, 0)",
   inverseSurface: "rgb(51, 48, 42)",
   inverseOnSurface: "rgb(247, 240, 231)",
   inversePrimary: "rgb(242, 192, 42)",
   elevation: {
    level0: "transparent",
    level1: "rgb(248, 243, 242)",
    level2: "rgb(244, 238, 235)",
    level3: "rgb(240, 233, 227)",
    level4: "rgb(239, 232, 224)",
    level5: "rgb(236, 229, 219)",
   },
   surfaceDisabled: "rgba(30, 27, 22, 0.12)",
   onSurfaceDisabled: "rgba(30, 27, 22, 0.38)",
   backdrop: "rgba(53, 48, 36, 0.4)",
  },
 },
 darkTheme: {
  colors: {
   primary: "rgb(242, 192, 42)",
   onPrimary: "rgb(62, 46, 0)",
   primaryContainer: "rgb(89, 68, 0)",
   onPrimaryContainer: "rgb(255, 223, 147)",
   secondary: "rgb(239, 193, 72)",
   onSecondary: "rgb(62, 46, 0)",
   secondaryContainer: "rgb(90, 68, 0)",
   onSecondaryContainer: "rgb(255, 223, 151)",
   tertiary: "rgb(142, 205, 255)",
   onTertiary: "rgb(0, 52, 79)",
   tertiaryContainer: "rgb(0, 75, 112)",
   onTertiaryContainer: "rgb(202, 230, 255)",
   error: "rgb(255, 180, 171)",
   onError: "rgb(105, 0, 5)",
   errorContainer: "rgb(147, 0, 10)",
   onErrorContainer: "rgb(255, 180, 171)",
   background: "rgb(30, 27, 22)",
   onBackground: "rgb(233, 225, 217)",
   surface: "rgb(30, 27, 22)",
   onSurface: "rgb(233, 225, 217)",
   surfaceVariant: "rgb(76, 70, 57)",
   onSurfaceVariant: "rgb(207, 197, 180)",
   outline: "rgb(152, 144, 128)",
   outlineVariant: "rgb(76, 70, 57)",
   shadow: "rgb(0, 0, 0)",
   scrim: "rgb(0, 0, 0)",
   inverseSurface: "rgb(233, 225, 217)",
   inverseOnSurface: "rgb(51, 48, 42)",
   inversePrimary: "rgb(118, 91, 0)",
   elevation: {
    level0: "transparent",
    level1: "rgb(41, 35, 23)",
    level2: "rgb(47, 40, 24)",
    level3: "rgb(53, 45, 24)",
    level4: "rgb(55, 47, 24)",
    level5: "rgb(60, 50, 25)",
   },
   surfaceDisabled: "rgba(233, 225, 217, 0.12)",
   onSurfaceDisabled: "rgba(233, 225, 217, 0.38)",
   backdrop: "rgba(53, 48, 36, 0.4)",
  },
 },
};
